// TSIA2 score report -- the platform's report, not the test's.
//
// Structure, class names and components follow DigitalSHSATReport so this
// reads as the same report a student sees for every other test type: the back
// bar, the logo/name/date header, "Test Result Overview" with REVIEW QUESTIONS
// beside it, the horizontal score band, then Category Scores with the Level 1
// bars expanding to their Level 2 rows on click.
//
// What is deliberately absent: every timing panel the other reports carry --
// the Section Time Averages gauges, the per-question time targets, the
// time-spent colouring. TSIA2 is untimed, so pace is not a thing that
// happened, and a panel reporting on it would be inventing data.
//
// In its place, each section names the stages the student was actually served.
// That is the fact the timing panel used to occupy space with: whether the
// student met the CRC cutoff or was routed into the Diagnostic.
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { MdOutlineArrowBack } from 'react-icons/md';
import moment from 'moment';

import axios from 'config/axios';
import ButtonLink from 'components/ButtonLink';
import LevelBar from 'components/LevelBar';
import Loader from 'components/Loader';
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { MULTIPLE_CHOICES } from 'global/constants';
import { setError } from 'store/error';

import './index.css';

type Category = { name: string; numCorrect: number; numQuestions: number };

type RootCategory = Category & { categories: Category[] };

type AnswerRow = {
    questionNumber: number;
    choice: number | null;
    correctChoice: number | null;
    isCorrect: boolean;
};

type Stage = {
    moduleName: string;
    moduleNumber: number;
    correct: number;
    total: number;
    // Per stage, not per section: weak categories on the stage that routed a
    // student are a different question from weak categories on the stage they
    // were routed into, and averaging the two hides both.
    rootCategories: RootCategory[];
    answers: AnswerRow[];
};

type ReportSection = {
    subject: string;
    sectionName: string;
    rawCorrect: number;
    questionCount: number;
    score: number | null;
    stages: Stage[];
};

type ReportData = {
    studentTestId: string;
    name: string;
    studentName: string;
    logo: string | null;
    testDate: string;
    score: number | null;
    rawCorrect: number;
    totalQuestionCount: number;
    testSections: ReportSection[];
};

function percentOf(correct: number, total: number) {
    return total ? Math.round((correct / total) * 100) : 0;
}

function DigitalTSIA2Report() {
    const [testData, setTestData] = useState<ReportData | null>(null);
    // Which Level 1's Level 2 breakdown is expanded, mirroring the SAT and
    // SHSAT reports.
    const [rootCategoryName, setRootCategoryName] = useState('');
    // Which section and which of its stages the Questions Overview is showing.
    const [sectionIndex, setSectionIndex] = useState(0);
    const [stageIndex, setStageIndex] = useState(0);

    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { organization } = useAppSelector(state => state.organization);
    const { testId } = useParams();

    useEffect(() => {
        let cancelled = false;
        axios.get(`tests/digital/result/tsia2/${testId}/`)
            .then(response => { if (!cancelled) setTestData(response.data); })
            .catch(error => {
                if (cancelled) return;
                dispatch(setError(error?.response?.data?.message ?? 'Could not load this report.'));
                navigate('/tests');
            });
        return () => { cancelled = true; };
    }, [testId, dispatch, navigate]);

    if (!testData) return <Loader />;

    const activeSection = testData.testSections[sectionIndex] ?? testData.testSections[0];
    const activeStage = activeSection?.stages[stageIndex] ?? activeSection?.stages[0];
    // Same gate the other reports use for the key column.
    const showAnswers = !!organization.customizations.find(
        customization => customization === 'Enable Show Answer',
    );

    return (
        // `layout` is the shared page wrapper every report uses: it clears the
        // fixed navbar and applies the house max-width, so this page sits
        // exactly where the others do.
        <div className='layout'>
            <Helmet><title>{testData.name} Report</title></Helmet>

            <div className='digitalTSIA2Report__bar'>
                <button className='digitalTSIA2Report__back' onClick={() => navigate('/tests')}>
                    <MdOutlineArrowBack className='digitalTSIA2Report__backIcon' />
                    Back to My Tests
                </button>
            </div>

            <div className='digitalTSIA2Report__headerGroup'>
                <div className='digitalTSIA2Report__headerSection'>
                    {testData.logo && (
                        <img className='digitalTSIA2Report__headerLogo' alt='logo' src={testData.logo} />
                    )}
                </div>
                <div className='digitalTSIA2Report__headerSection'>
                    <div className='digitalTSIA2Report__headerInfo'>
                        <p>Name: {testData.studentName}</p>
                        <p>Test name: {testData.name}</p>
                        <p>Tested on: {moment(testData.testDate).format('MMMM D YYYY')}</p>
                    </div>
                </div>
            </div>

            <div className='digitalTSIA2Report__headerGroup'>
                <h1 className='digitalTSIA2Report__headerTitle'>Test Result Overview</h1>
                <div className='digitalTSIA2Report__buttonGroup'>
                    <ButtonLink
                        className='digitalTSIA2Report__button'
                        to={`/review/digital-tsia2/${testId}`}
                    >
                        REVIEW QUESTIONS
                    </ButtonLink>
                </div>
            </div>

            <div className='digitalTSIA2Report__card'>
                <div className='digitalTSIA2Report__cardRow'>
                    {/* Score band: the whole test, then each section. TSIA2 is
                        scored as a flat percentage, so the headline is the
                        percent with the raw count beneath it -- no scaled
                        score, because the test has none. */}
                    <div className='digitalTSIA2Report__scoresGroup'>
                        <div className='digitalTSIA2Report__scoreBlock'>
                            <h2 className='digitalTSIA2Report__scoreTitle'>TOTAL SCORE</h2>
                            <div className='digitalTSIA2Report__scorePercent'>
                                {percentOf(testData.rawCorrect, testData.totalQuestionCount)}
                                <span style={{ fontSize: '0.5em', fontWeight: 400 }}>%</span>
                            </div>
                            <div className='digitalTSIA2Report__scoreRaw'>
                                {testData.rawCorrect}/{testData.totalQuestionCount} correct
                            </div>
                        </div>
                        {testData.testSections.map((testSection, t) => (
                            <div className='digitalTSIA2Report__scoreBlock' key={t}>
                                <h3 className='digitalTSIA2Report__sectionScoreTitle'>
                                    {testSection.subject}
                                </h3>
                                <div className='digitalTSIA2Report__scorePercent'>
                                    {percentOf(testSection.rawCorrect, testSection.questionCount)}
                                    <span style={{ fontSize: '0.5em', fontWeight: 400 }}>%</span>
                                </div>
                                <div className='digitalTSIA2Report__scoreRaw'>
                                    {testSection.rawCorrect}/{testSection.questionCount} correct
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className='digitalTSIA2Report__overview'>
                        {testData.testSections.some(
                            section => section.stages.some(stage => stage.rootCategories?.length > 0),
                        ) && (<>
                            <h2 className='digitalTSIA2Report__cardSubTitle'>Category Scores</h2>
                            <div className='digitalTSIA2Report__overviewRow'>
                                {testData.testSections.flatMap((testSection, s) =>
                                    testSection.stages.map(stage => (
                                    <div
                                        className='digitalTSIA2Report__overviewItem'
                                        key={`${testSection.subject}-${stage.moduleName}`}
                                    >
                                        <h4 className='digitalTSIA2Report__overviewLabel'>
                                            {stage.moduleName}
                                        </h4>
                                        {stage.rootCategories.map(rootCategory => (
                                            <React.Fragment key={rootCategory.name}>
                                                <h5
                                                    className={rootCategoryName === `${stage.moduleName}-${rootCategory.name}`
                                                        ? 'digitalTSIA2Report__levelLabel--active'
                                                        : 'digitalTSIA2Report__levelLabel'}
                                                    onClick={() => setRootCategoryName(
                                                        rootCategoryName === `${stage.moduleName}-${rootCategory.name}`
                                                            ? '' : `${stage.moduleName}-${rootCategory.name}`,
                                                    )}
                                                >
                                                    {rootCategory.name}
                                                    <div className={rootCategoryName === `${stage.moduleName}-${rootCategory.name}`
                                                        ? 'digitalTSIA2Report__levelDetailed--active'
                                                        : 'digitalTSIA2Report__levelDetailed'}
                                                    >
                                                        {(rootCategory.categories ?? []).map(category => (
                                                            <div
                                                                className='digitalTSIA2Report__levelDetailedItem'
                                                                key={category.name}
                                                            >
                                                                <p>{category.name}</p>
                                                                <p className={
                                                                    category.numCorrect / category.numQuestions >= 0.8
                                                                        ? 'digitalTSIA2Report__levelDetailedGreen'
                                                                        : category.numCorrect / category.numQuestions >= 0.4
                                                                        ? 'digitalTSIA2Report__levelDetailedYellow'
                                                                        : 'digitalTSIA2Report__levelDetailedRed'
                                                                }>
                                                                    {category.numCorrect}/{category.numQuestions}
                                                                </p>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </h5>
                                                <LevelBar
                                                    className={s % 2 === 0
                                                        ? 'digitalTSIA2Report__levelGreen'
                                                        : 'digitalTSIA2Report__levelOrange'}
                                                    percent={rootCategory.numQuestions
                                                        ? 100 * rootCategory.numCorrect / rootCategory.numQuestions
                                                        : 0}
                                                    label={`${rootCategory.numCorrect}/${rootCategory.numQuestions}`}
                                                />
                                            </React.Fragment>
                                        ))}
                                    </div>
                                )))}
                            </div>
                        </>)}
                    </div>
                </div>

                {/* Where the other reports put Section Time Averages. TSIA2 is
                    untimed, so this reports the routing instead. */}
                <h2 className='digitalTSIA2Report__cardSubTitle' style={{ marginTop: 30 }}>
                    Stages Taken
                </h2>
                <div className='digitalTSIA2Report__overviewRow'>
                    {testData.testSections.map((testSection, s) => (
                        <div className='digitalTSIA2Report__overviewItem' key={s}>
                            <h4 className='digitalTSIA2Report__overviewLabel'>
                                {testSection.subject}
                            </h4>
                            <table className='digitalTSIA2Report__stageTable'>
                                <tbody>
                                    {testSection.stages.map(stage => (
                                        <tr key={stage.moduleName}>
                                            <td>{stage.moduleName}</td>
                                            <td>{stage.correct}/{stage.total} correct</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ))}
                </div>
            </div>

            {/* Questions Overview: section tabs, then a tab per stage, then
                the answer table -- the same block the other reports carry,
                with Time Spent dropped because the test is untimed. */}
            <div className='digitalTSIA2Report__questions'>
                <h2 className='digitalTSIA2Report__questionsHeader'>Questions Overview</h2>

                <div className='digitalTSIA2Report__sectionTabs'>
                    {testData.testSections.map((testSection, t) => (
                        <button
                            className={sectionIndex === t
                                ? 'digitalTSIA2Report__sectionTab--active'
                                : 'digitalTSIA2Report__sectionTab'}
                            onClick={() => { setSectionIndex(t); setStageIndex(0); }}
                            key={testSection.subject}
                        >
                            {testSection.subject}
                        </button>
                    ))}
                    <div className={sectionIndex === 0
                        ? 'digitalTSIA2Report__sectionTabIndicatorLeft'
                        : 'digitalTSIA2Report__sectionTabIndicatorRight'}
                    />
                </div>

                {/* Only shown when the student was routed: a student who met
                    the cutoff has one stage and needs no tab to choose it. */}
                {activeSection.stages.length > 1 && (
                    <div className='digitalTSIA2Report__moduleTabs'>
                        {activeSection.stages.map((stage, t) => (
                            <button
                                className={stageIndex === t
                                    ? 'digitalTSIA2Report__moduleTab--active'
                                    : 'digitalTSIA2Report__moduleTab'}
                                onClick={() => setStageIndex(t)}
                                key={stage.moduleName}
                            >
                                {stage.moduleName}
                            </button>
                        ))}
                        <div className={stageIndex === 0
                            ? 'digitalTSIA2Report__moduleTabIndicatorLeft'
                            : 'digitalTSIA2Report__moduleTabIndicatorRight'}
                        />
                    </div>
                )}

                {activeStage && (
                    <div className='digitalTSIA2Report__questionsOverview'>
                        <div className='digitalTSIA2Report__questionsSummary'>
                            <div className='digitalTSIA2Report__questionsSummaryItem'>
                                <p className='digitalTSIA2Report__questionSummaryNumber'>
                                    {activeStage.answers.length}
                                </p>
                                <p className='digitalTSIA2Report__questionSummaryText'>
                                    Total Questions
                                </p>
                            </div>
                            <div className='digitalTSIA2Report__questionsSummaryItem'>
                                <p className='digitalTSIA2Report__questionSummaryNumber'>
                                    {activeStage.answers.filter(answer => answer.isCorrect).length}
                                </p>
                                <p className='digitalTSIA2Report__questionSummaryText'>
                                    Correct Answers
                                </p>
                            </div>
                            <div className='digitalTSIA2Report__questionsSummaryItem'>
                                <p className='digitalTSIA2Report__questionSummaryNumber'>
                                    {activeStage.answers.filter(answer => !answer.isCorrect).length}
                                </p>
                                <p className='digitalTSIA2Report__questionSummaryText'>
                                    Incorrect Answers
                                </p>
                            </div>
                        </div>

                        <table className='digitalTSIA2Report__questionsTable'>
                            <tbody>
                                <tr>
                                    <th>Question</th>
                                    {showAnswers && <th>Correct Answer</th>}
                                    <th>Your Answer</th>
                                </tr>
                                {activeStage.answers.map(answer => (
                                    <tr key={answer.questionNumber}>
                                        <td>{answer.questionNumber}</td>
                                        {showAnswers && (
                                            <td>
                                                {answer.correctChoice !== null
                                                    ? MULTIPLE_CHOICES[answer.correctChoice]
                                                    : '-'}
                                            </td>
                                        )}
                                        <td className={answer.isCorrect
                                            ? 'digitalTSIA2Report__answerCorrect'
                                            : 'digitalTSIA2Report__answerIncorrect'}
                                        >
                                            {answer.choice !== null
                                                ? MULTIPLE_CHOICES[answer.choice]
                                                : '-'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

export default DigitalTSIA2Report;
